import { SportType } from '../types/event';

export type LevelId = 'level_1' | 'level_2' | 'level_3' | 'level_4' | 'level_5';

export interface Level {
  id: LevelId; // Generic ID: "level_1" to "level_5"
  genericName: string; // Generic name: Beginner, Elementary, Intermediate, Advanced, Expert
  range: string; // Sport-specific range: "1.0–1.5", "HC >36", etc.
  description: string; // Sport-specific description
  type?: string; // Additional info: format, venue, etc.
  capacityLimit?: string; // Capacity limit
  description2?: string; // Description 2
}

// Generic level names (same across all sports)
const GENERIC_LEVEL_NAMES: Record<LevelId, string> = {
  level_1: 'Beginner',
  level_2: 'Elementary',
  level_3: 'Intermediate',
  level_4: 'Advanced',
  level_5: 'Expert',
};

// Sport-specific level definitions
const SPORT_LEVELS: Record<SportType, Level[]> = {
  Tennis: [
    {
      id: 'level_1',
      genericName: GENERIC_LEVEL_NAMES.level_1,
      range: '1.0–1.5',
      description: 'Beginner; just learned to swing, can rally a few shots, low consistency',
      type: 'Format: Singles (Women\'s/Men\'s), Doubles, Mixed Doubles',
      description2: 'Venue: Indoor, Outdoor',
    },
    {
      id: 'level_2',
      genericName: GENERIC_LEVEL_NAMES.level_2,
      range: '2.0–2.5',
      description: 'Can complete 5–10 rally exchanges with basic control',
      type: 'Format: Singles (Women\'s/Men\'s), Doubles, Mixed Doubles',
      description2: 'Venue: Indoor, Outdoor',
    },
    {
      id: 'level_3',
      genericName: GENERIC_LEVEL_NAMES.level_3,
      range: '3.0–3.5',
      description: 'Can hit with spin, beginning tactical awareness, can compete',
      type: 'Format: Singles (Women\'s/Men\'s), Doubles, Mixed Doubles',
      description2: 'Venue: Indoor, Outdoor',
    },
    {
      id: 'level_4',
      genericName: GENERIC_LEVEL_NAMES.level_4,
      range: '4.0–4.5',
      description: 'Well-rounded technique, tactical organization, can compete in matches',
      type: 'Format: Singles (Women\'s/Men\'s), Doubles, Mixed Doubles',
      description2: 'Venue: Indoor, Outdoor',
    },
    {
      id: 'level_5',
      genericName: GENERIC_LEVEL_NAMES.level_5,
      range: '5.0+',
      description: 'Near-professional or professional level',
      type: 'Format: Singles (Women\'s/Men\'s), Doubles, Mixed Doubles',
      description2: 'Venue: Indoor, Outdoor',
    },
  ],

  Badminton: [
    {
      id: 'level_1',
      genericName: GENERIC_LEVEL_NAMES.level_1,
      range: '0.5',
      description: 'Beginner; can get the shuttle over the net',
      type: 'Format: Singles (Women\'s/Men\'s), Doubles, Mixed Doubles',
      description2: 'Venue: Indoor, Outdoor',
    },
    {
      id: 'level_2',
      genericName: GENERIC_LEVEL_NAMES.level_2,
      range: '1.0–1.5',
      description: 'Stable clears, 5–10 rally exchanges',
      type: 'Format: Singles (Women\'s/Men\'s), Doubles, Mixed Doubles',
      description2: 'Venue: Indoor, Outdoor',
    },
    {
      id: 'level_3',
      genericName: GENERIC_LEVEL_NAMES.level_3,
      range: '2.0–2.5',
      description: 'Can smash and play net shots, basic doubles coordination',
      type: 'Format: Singles (Women\'s/Men\'s), Doubles, Mixed Doubles',
      description2: 'Venue: Indoor, Outdoor',
    },
    {
      id: 'level_4',
      genericName: GENERIC_LEVEL_NAMES.level_4,
      range: '3.0–3.5',
      description: 'Balanced attack and defense, tactical play, can sustain 15+ rally exchanges',
      type: 'Format: Singles (Women\'s/Men\'s), Doubles, Mixed Doubles',
      description2: 'Venue: Indoor, Outdoor',
    },
    {
      id: 'level_5',
      genericName: GENERIC_LEVEL_NAMES.level_5,
      range: '4.0–4.5',
      description: 'Competitive level, strong results in amateur tournaments',
      type: 'Format: Singles (Women\'s/Men\'s), Doubles, Mixed Doubles',
      description2: 'Venue: Indoor, Outdoor',
    },
  ],

  Golf: [
    {
      id: 'level_1',
      genericName: GENERIC_LEVEL_NAMES.level_1,
      range: 'HC >36',
      description: 'Beginner; can make contact with the ball',
      type: 'Venue: 18-hole, 9-hole, Driving Range, Simulator',
      description2: 'Type (optional): Stroke Play, Match Play, Casual',
    },
    {
      id: 'level_2',
      genericName: GENERIC_LEVEL_NAMES.level_2,
      range: 'HC 28–36',
      description: 'Can complete a full 18 holes with a high score',
      type: 'Venue: 18-hole, 9-hole, Driving Range, Simulator',
      description2: 'Type (optional): Stroke Play, Match Play, Casual',
    },
    {
      id: 'level_3',
      genericName: GENERIC_LEVEL_NAMES.level_3,
      range: 'HC 18–27',
      description: 'Typical score 110–120 strokes',
      type: 'Venue: 18-hole, 9-hole, Driving Range, Simulator',
      description2: 'Type (optional): Stroke Play, Match Play, Casual',
    },
    {
      id: 'level_4',
      genericName: GENERIC_LEVEL_NAMES.level_4,
      range: 'HC 10–17',
      description: 'Typical score 90–100 strokes, stable short game',
      type: 'Venue: 18-hole, 9-hole, Driving Range, Simulator',
      description2: 'Type (optional): Stroke Play, Match Play, Casual',
    },
    {
      id: 'level_5',
      genericName: GENERIC_LEVEL_NAMES.level_5,
      range: 'HC 0–9',
      description: 'Under 85 strokes, competitive ability',
      type: 'Venue: 18-hole, 9-hole, Driving Range, Simulator',
      description2: 'Type (optional): Stroke Play, Match Play, Casual',
    },
  ],

  Bouldering: [
    {
      id: 'level_1',
      genericName: GENERIC_LEVEL_NAMES.level_1,
      range: 'V0–V2 (YDS ≤5.9)',
      description: 'Beginner; simple moves; basic strength and footwork required',
      type: 'Venue: Indoor, Outdoor',
      description2: 'Style (optional): Dyno, Endurance, Power',
    },
    {
      id: 'level_2',
      genericName: GENERIC_LEVEL_NAMES.level_2,
      range: 'V3–V5 (YDS 5.10a–5.11d)',
      description: 'Intermediate; dynamic moves; power and technique',
      type: 'Venue: Indoor, Outdoor',
      description2: 'Style (optional): Dyno, Endurance, Power',
    },
    {
      id: 'level_3',
      genericName: GENERIC_LEVEL_NAMES.level_3,
      range: 'V6–V7 (YDS 5.12a–5.12d)',
      description: 'Advanced; precise and complex moves',
      type: 'Venue: Indoor, Outdoor',
      description2: 'Style (optional): Dyno, Endurance, Power',
    },
    {
      id: 'level_4',
      genericName: GENERIC_LEVEL_NAMES.level_4,
      range: 'V8–V9 (YDS 5.13a–5.13d)',
      description: 'Advanced level; can complete high-difficulty routes',
      type: 'Venue: Indoor, Outdoor',
      description2: 'Style (optional): Dyno, Endurance, Power',
    },
    {
      id: 'level_5',
      genericName: GENERIC_LEVEL_NAMES.level_5,
      range: 'V10+ (YDS 5.14a+)',
      description: 'Expert level; near professional',
      type: 'Venue: Indoor, Outdoor',
      description2: 'Style (optional): Dyno, Endurance, Power',
    },
  ],

  Pickleball: [
    {
      id: 'level_1',
      genericName: GENERIC_LEVEL_NAMES.level_1,
      range: '1.0–2.0',
      description: 'Beginner; can rally',
      type: 'Format: Singles (Women\'s/Men\'s), Doubles, Mixed Doubles',
      description2: 'Venue: Indoor, Outdoor',
    },
    {
      id: 'level_2',
      genericName: GENERIC_LEVEL_NAMES.level_2,
      range: '2.5',
      description: 'Can dink; steady rally exchanges',
      type: 'Format: Singles (Women\'s/Men\'s), Doubles, Mixed Doubles',
      description2: 'Venue: Indoor, Outdoor',
    },
    {
      id: 'level_3',
      genericName: GENERIC_LEVEL_NAMES.level_3,
      range: '3.0–3.5',
      description: 'Tactics and teamwork; can compete',
      type: 'Format: Singles (Women\'s/Men\'s), Doubles, Mixed Doubles',
      description2: 'Venue: Indoor, Outdoor',
    },
    {
      id: 'level_4',
      genericName: GENERIC_LEVEL_NAMES.level_4,
      range: '4.0',
      description: 'Competitive level; can play in tournaments',
      type: 'Format: Singles (Women\'s/Men\'s), Doubles, Mixed Doubles',
      description2: 'Venue: Indoor, Outdoor',
    },
    {
      id: 'level_5',
      genericName: GENERIC_LEVEL_NAMES.level_5,
      range: '4.5+',
      description: 'Advanced competitive level; tournament experience',
      type: 'Format: Singles (Women\'s/Men\'s), Doubles, Mixed Doubles',
      description2: 'Venue: Indoor, Outdoor',
    },
  ],

  Gym: [
    {
      id: 'level_1',
      genericName: GENERIC_LEVEL_NAMES.level_1,
      range: 'Beginner',
      description: 'Less than 0.5 years experience; just started training, form not standard, needs guidance',
      type: 'Type: Strength, Cardio, CrossFit, HIIT',
      description2: 'Venue: Gym, Outdoor',
    },
    {
      id: 'level_2',
      genericName: GENERIC_LEVEL_NAMES.level_2,
      range: 'Elementary',
      description: '0.5–1.5 years experience; can complete basic workouts with some form awareness',
      type: 'Type: Strength, Cardio, CrossFit, HIIT',
      description2: 'Venue: Gym, Outdoor',
    },
    {
      id: 'level_3',
      genericName: GENERIC_LEVEL_NAMES.level_3,
      range: 'Intermediate',
      description: '1.5–3 years experience; progressive overload training, specific goals (muscle gain/fat loss)',
      type: 'Type: Strength, Cardio, CrossFit, HIIT',
      description2: 'Goals: Muscle gain, fat loss, general fitness',
    },
    {
      id: 'level_4',
      genericName: GENERIC_LEVEL_NAMES.level_4,
      range: 'Advanced',
      description: '3–5 years experience; can program workouts, break plateaus, well-rounded development',
      type: 'Type: Strength, Cardio, CrossFit, HIIT',
      description2: 'Goals: Muscle gain, fat loss, general fitness',
    },
    {
      id: 'level_5',
      genericName: GENERIC_LEVEL_NAMES.level_5,
      range: 'Competitive/Pro',
      description: '5+ years experience; solid form, strength/physique at professional level, may compete',
      type: 'Type: Strength, Cardio, CrossFit, HIIT',
      description2: 'Goals: Muscle gain, fat loss, general fitness',
    },
  ],

  Hiking: [
    {
      id: 'level_1',
      genericName: GENERIC_LEVEL_NAMES.level_1,
      range: 'Grade 1',
      description: '3–5 km easy trail',
      type: 'Terrain: Forest, Alpine, Snow Mountain, Desert, Canyon, Lakeside',
      description2: 'Style: Light hiking, Backpacking, Thru-hike',
    },
    {
      id: 'level_2',
      genericName: GENERIC_LEVEL_NAMES.level_2,
      range: 'Grade 2',
      description: '10 km with elevation gain',
      type: 'Terrain: Forest, Alpine, Snow Mountain, Desert, Canyon, Lakeside',
      description2: 'Style: Light hiking, Backpacking, Thru-hike',
    },
    {
      id: 'level_3',
      genericName: GENERIC_LEVEL_NAMES.level_3,
      range: 'Grade 3',
      description: '20+ km, moderate difficulty',
      type: 'Terrain: Forest, Alpine, Snow Mountain, Desert, Canyon, Lakeside',
      description2: 'Style: Light hiking, Backpacking, Thru-hike',
    },
    {
      id: 'level_4',
      genericName: GENERIC_LEVEL_NAMES.level_4,
      range: 'Grade 4',
      description: '20–30 km backpacking, 2–3 days',
      type: 'Terrain: Forest, Alpine, Snow Mountain, Desert, Canyon, Lakeside',
      description2: 'Style: Light hiking, Backpacking, Thru-hike',
    },
    {
      id: 'level_5',
      genericName: GENERIC_LEVEL_NAMES.level_5,
      range: 'Grade 5',
      description: 'High altitude / long distance (e.g. 50km+ alpine)',
      type: 'Terrain: Forest, Alpine, Snow Mountain, Desert, Canyon, Lakeside',
      description2: 'Style: Light hiking, Backpacking, Thru-hike',
    },
  ],

  'Ski/Snowboarding': [
    {
      id: 'level_1',
      genericName: GENERIC_LEVEL_NAMES.level_1,
      range: 'Green',
      description: 'Beginner slope; 6–25% gradient; learning to brake and turn',
      type: 'Slope difficulty: Green, Blue, Black, Double Black',
      description2: 'Terrain/Style: Tree skiing, Backcountry, Park, Flat trick, Carving',
    },
    {
      id: 'level_2',
      genericName: GENERIC_LEVEL_NAMES.level_2,
      range: 'Blue',
      description: 'Intermediate slope; 25–40% gradient; can control speed and turn',
      type: 'Slope difficulty: Green, Blue, Black, Double Black',
      description2: 'Terrain/Style: Tree skiing, Backcountry, Park, Flat trick, Carving',
    },
    {
      id: 'level_3',
      genericName: GENERIC_LEVEL_NAMES.level_3,
      range: 'Black',
      description: 'Advanced slope; 40%+ gradient; requires solid technique',
      type: 'Slope difficulty: Green, Blue, Black, Double Black',
      description2: 'Terrain/Style: Tree skiing, Backcountry, Park, Flat trick, Carving',
    },
    {
      id: 'level_4',
      genericName: GENERIC_LEVEL_NAMES.level_4,
      range: 'Double Black',
      description: 'Expert level; very steep or with obstacles',
      type: 'Slope difficulty: Green, Blue, Black, Double Black',
      description2: 'Terrain/Style: Tree skiing, Backcountry, Park, Flat trick, Carving',
    },
    {
      id: 'level_5',
      genericName: GENERIC_LEVEL_NAMES.level_5,
      range: 'Tree skiing / Backcountry',
      description: 'Skiing through trees or backcountry off-piste; requires advanced board control and avalanche awareness',
      type: 'Slope difficulty: Green, Blue, Black, Double Black',
      description2: 'Terrain/Style: Tree skiing, Backcountry, Park, Flat trick, Carving',
    },
  ],
};

/**
 * Get all 5 levels for a specific sport
 */
export function getLevelsForSport(sport: SportType): Level[] {
  return SPORT_LEVELS[sport] || [];
}

/**
 * Get a specific level by ID for a sport
 */
export function getLevelById(levelId: LevelId, sport: SportType): Level | undefined {
  const levels = getLevelsForSport(sport);
  return levels.find(level => level.id === levelId);
}

/**
 * Get display name for a level (sport-specific range)
 */
export function getLevelDisplayName(levelId: LevelId | string | undefined, sport: SportType | string | undefined): string {
  if (!levelId || !sport) {
    return 'Any Level';
  }

  const level = getLevelById(levelId as LevelId, sport as SportType);
  if (level) {
    return level.range;
  }

  // Fallback to generic name
  return getGenericLevelName(levelId as LevelId);
}

/**
 * Get generic level name (Beginner, Elementary, etc.)
 */
export function getGenericLevelName(levelId: LevelId | string | undefined): string {
  if (!levelId || !(levelId in GENERIC_LEVEL_NAMES)) {
    return 'Any Level';
  }
  return GENERIC_LEVEL_NAMES[levelId as LevelId];
}

/**
 * Get full level information for display
 */
export function getLevelInfo(levelId: LevelId | string | undefined, sport: SportType | string | undefined): Level | null {
  if (!levelId || !sport) {
    return null;
  }
  return getLevelById(levelId as LevelId, sport as SportType) || null;
}
